(() => {
  if (customElements.get('malzauber-offers')) return;

  class MalzauberOffers extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.listeners = new AbortController();
      this.timers = new Set();
      this.storageKey = 'malzauber-offers-v1';
      this.startedAt = Date.now();
      this.interacted = false;
      this.desktop = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.newsletterVisible = this.dataset.newsletterVisible === 'true';
      this.storageAvailable = this.checkStorage();
      const options = { signal: this.listeners.signal };

      this.querySelectorAll('[data-mzo-open]').forEach((button) => {
        if (!button.hasAttribute('data-mzo-teaser')) button.hidden = false;
        button.addEventListener('click', () => this.openOffer(false), options);
      });
      this.querySelector('[data-mzo-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => {
        if (this.dialog.open) return;
        this.stopWatchingCaptcha();
        this.captchaPaused = false;
        this.dialog.inert = false;
        this.dialog.classList.remove('mzo-dialog--captcha');
        const originalVisible = this.returnFocus?.isConnected && this.returnFocus !== document.body && this.returnFocus.getClientRects().length > 0;
        const target = originalVisible ? this.returnFocus : this.querySelector('[data-mzo-open]:not([data-mzo-teaser])');
        target?.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.querySelector('form').addEventListener('submit', () => this.setFlag('submitted', true), options);

      for (const name of ['pointerdown', 'keydown', 'scroll']) {
        window.addEventListener(name, () => {
          this.interacted = true;
          if (Date.now() - this.startedAt >= 12000) this.showTeaser();
        }, { ...options, passive: true, once: true });
      }
      document.addEventListener('mouseout', (event) => {
        if (!event.relatedTarget && event.clientY <= 0 && this.desktop.matches && this.interacted && Date.now() - this.startedAt >= 12000) this.openOffer(true);
      }, options);
      window.addEventListener('pagehide', () => {
        this.setFlag('from_cart', this.isCart());
        this.clearTimers();
        this.stopWatchingCaptcha();
        if (this.dialog.open) this.dialog.close();
      }, options);
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.onPageShown();
      }, options);
      document.addEventListener('theme-drawer:close', (event) => {
        if (event.target.id === 'cart-drawer') this.afterCart();
      }, options);
      this.onPageShown();
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.clearTimers();
      this.stopWatchingCaptcha();
      this.ready = false;
    }

    watchCaptcha() {
      this.stopWatchingCaptcha();
      const schedule = () => {
        if (!this.dialog.open || this.captchaFrame) return;
        this.captchaFrame = requestAnimationFrame(() => {
          this.captchaFrame = 0;
          this.syncCaptchaLayer();
        });
      };
      this.captchaObserver = new MutationObserver(schedule);
      this.captchaObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'src'] });
      // CSS transitions can reveal the challenge without another DOM mutation.
      this.captchaPoll = window.setInterval(schedule, 200);
      schedule();
    }

    stopWatchingCaptcha() {
      this.captchaObserver?.disconnect();
      window.clearInterval(this.captchaPoll);
      this.captchaPoll = 0;
      if (this.captchaFrame) cancelAnimationFrame(this.captchaFrame);
      this.captchaFrame = 0;
    }

    visibleCaptchaChallenge() {
      return [...document.querySelectorAll('iframe[src*="hcaptcha.com/"][src*="frame=challenge"]')].find((frame) => {
        const rect = frame.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0 || !frame.getClientRects().length) return false;
        for (let element = frame; element instanceof HTMLElement; element = element.parentElement) {
          const style = getComputedStyle(element);
          if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
        }
        return true;
      });
    }

    syncCaptchaLayer() {
      if (!this.dialog.open) return;
      const challenge = this.visibleCaptchaChallenge();
      if (challenge && !this.captchaPaused) {
        this.captchaPaused = true;
        this.dialog.classList.add('mzo-dialog--captcha');
        this.dialog.close();
        this.dialog.inert = true;
        this.dialog.show();
        challenge.focus({ preventScroll: true });
      } else if (!challenge && this.captchaPaused) {
        this.captchaPaused = false;
        this.dialog.classList.remove('mzo-dialog--captcha');
        this.dialog.close();
        this.dialog.inert = false;
        this.dialog.showModal();
        this.querySelector('[data-mzo-close]').focus({ preventScroll: true });
      }
    }

    checkStorage() {
      try {
        sessionStorage.setItem(`${this.storageKey}:check`, '1');
        sessionStorage.removeItem(`${this.storageKey}:check`);
        return true;
      } catch {
        return false;
      }
    }

    getFlag(name) {
      try { return this.storageAvailable && sessionStorage.getItem(`${this.storageKey}:${name}`) === '1'; }
      catch { return false; }
    }

    setFlag(name, value) {
      try {
        if (!this.storageAvailable) return;
        if (value) sessionStorage.setItem(`${this.storageKey}:${name}`, '1');
        else sessionStorage.removeItem(`${this.storageKey}:${name}`);
      } catch { this.storageAvailable = false; }
    }

    isCart() {
      return location.pathname.replace(/\/$/, '') === this.dataset.cartPath.replace(/\/$/, '');
    }

    delay(callback, milliseconds) {
      const timer = window.setTimeout(() => { this.timers.delete(timer); callback(); }, milliseconds);
      this.timers.add(timer);
    }

    clearTimers() {
      this.timers?.forEach((timer) => window.clearTimeout(timer));
      this.timers?.clear();
    }

    onPageShown() {
      this.clearTimers();
      this.startedAt = Date.now();
      const result = this.querySelector('[data-mzo-result]');
      const ownSubmission = this.getFlag('submitted') || (!this.storageAvailable && location.hash === '#MalzauberNewsletter');
      if (result && ownSubmission) {
        this.setFlag('submitted', false);
        this.openOffer(false, true);
      }
      const returnedFromCart = !this.isCart() && this.getFlag('from_cart');
      this.setFlag('from_cart', this.isCart());
      if (returnedFromCart) this.afterCart();
      this.delay(() => this.showTeaser(), 12000);
    }

    afterCart() {
      this.interacted = true;
      this.delay(() => {
        if (this.desktop.matches) this.openOffer(true);
        else this.showTeaser();
      }, 1800);
    }

    showTeaser() {
      if (!this.newsletterVisible || this.getFlag('used') || this.dialog.open || !this.interacted) return;
      const teaser = this.querySelector('[data-mzo-teaser]');
      if (teaser) teaser.hidden = false;
    }

    openOffer(automatic, result = false) {
      if (this.dialog.open || (!this.newsletterVisible && !result)) return;
      if (automatic) {
        if (!this.storageAvailable || this.getFlag('used') || this.dataset.designMode === 'true' || !this.desktop.matches) return;
        if (document.visibilityState !== 'visible' || document.querySelector('dialog[open]')) return;
        if (document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')) return;
      }
      this.returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.setFlag('used', true);
      const teaser = this.querySelector('[data-mzo-teaser]');
      if (teaser) teaser.hidden = true;
      this.dialog.showModal();
      const focusTarget = result ? this.querySelector('[data-mzo-result]') : this.querySelector('[data-mzo-close]');
      focusTarget?.focus({ preventScroll: true });
      this.watchCaptcha();
    }
  }

  customElements.define('malzauber-offers', MalzauberOffers);
})();
